import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Save, User, X,
  Loader2, CheckCircle, AlertCircle, Sparkles,
  Bot, Briefcase, ArrowLeft,
  Zap, FileText,
  Wand2, Type, Brain, UserCircle, Boxes, RefreshCw
} from 'lucide-react';
import { agentService } from '../../services/PlatformService';
import AgentTools from '../../components/agent/AgentTools';
import SystemPromptEditor from '../../components/agent/SystemPromptEditor';
import FormBuilder from '../../components/agent/FormBuilder';
import WelcomeMessageConfig from '../../components/agent/WelcomeMessageConfig';
import KnowledgeBaseManager from '../../components/agent/KnowledgeBaseManager';
import TooltipConfig from '../../components/agent/TooltipConfig';
import AgentIdentityCard from '../../components/agent/AgentIdentityCard';

/**
 * Unified Agent Manager Component with Integrated Chat Preview
 */
const AgentManager = () => {
  const location = useLocation();
  const params = useParams();
  const queryParams = new URLSearchParams(location.search);
  const agentId = queryParams.get('id') || params.id;
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  // Tab states
  const [activeTab, setActiveTab] = useState('profile');
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Agent form data
  const [formData, setFormData] = useState({
    // Profile tab
    name: '',
    logo_url: '',
    primaryColor: '#A020F0',
    secondaryColor: '#E0E7FF',
    
    // Settings/Instructions combined
    system: '',
    provider: 'AzureOpenAI',
    model: 'gpt-4.1',
    creativity: 0.7,
    max_tokens: 1024,
    tools: [],
    
    // Widget settings (merged)
    widget_settings: {
      form: {
        active: false,
        inputs: []
      },
      welcome_message: 'Olá! 👋 Como posso ajudar você hoje?',
      tooltipMessage: "",
      tooltipPosition: "top",
      tooltipBackgroundColor: "white",
      tooltipTextColor: "grey"
    },
    knowledges: []
  });

  const [agent, setAgent] = useState(null);

  /**
   * Load agent data for editing
   */
  const loadAgentData = useCallback(async () => {
    if (!agentId) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await agentService.getAgentById(agentId);
      const agentData = response.data;
      
      setFormData({
        name: agentData.name || '',
        logo_url: agentData.widget_settings?.logo_url || '',
        primaryColor: agentData.widget_settings?.primary_color || '#A020F0',
        secondaryColor: agentData.widget_settings?.secondary_color || '#E0E7FF',
        system: agentData.system || '',
        provider: agentData.provider || 'AzureOpenAI',
        model: agentData.model || 'gpt-4.1',
        creativity: agentData.creativity || 0.7,
        max_tokens: agentData.max_tokens || 1024,
        tools: agentData.tools || [],
        widget_settings: agentData.widget_settings || {},
        knowledges: agentData.knowledges || []
      });

      setAgent(agentData);
      setIsEditMode(true);
    } catch (err) {
      console.error('Error loading agent data:', err);
      setError(err.response?.data?.detail || 'Não foi possível carregar os dados do funcionário.');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    if (agentId) {
      loadAgentData();
    }
  }, [agentId, loadAgentData]);

  /**
   * Handle iframe communication
   */
  useEffect(() => {
    const handleMessage = (event) => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      switch (event.data.type) {
        case "agent-chat-resize":
          // Aplica TODOS os estilos enviados pelo iframe
          iframe.style.width = event.data.width;
          iframe.style.height = event.data.height;
          iframe.style.bottom = event.data.bottom;
          iframe.style.right = event.data.right;
          iframe.style.borderRadius = event.data.borderRadius;
          break;

        case "agent-chat-opened":
          setIsChatOpen(true);
          break;

        case "agent-chat-closed":
          setIsChatOpen(false);
          break;

        case "agent-chat-ready":
          // Detecta mobile no parent e envia para o iframe
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
                          || window.innerWidth <= 768;
          
          iframe.contentWindow.postMessage({
            type: 'parent-info',
            isMobile: isMobile,
            screenWidth: window.innerWidth
          }, '*');
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  /**
   * Reload iframe to apply changes
   */
  const reloadChatPreview = () => {
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = '';
      setTimeout(() => {
        iframeRef.current.src = currentSrc;
      }, 100);
    }
  };

  /**
   * Handle form field changes
   */
  const handleFieldChange = (field, value, forceSaveTool) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (forceSaveTool) {
      handleSave(value);
    }
  };

  /**
   * Save agent data
   */
  const handleSave = async (forceSaveTools) => {
    // Validate required fields
    if (!formData.name.trim()) {
      setError('O nome do funcionário é obrigatório.');
      return;
    }

    if (!formData.system.trim()) {
      setError('As instruções de trabalho são obrigatórias.');
      return;
    }
    
    const agentData = {
      name: formData.name.trim(),
      description: '',
      tags: [],
      system: formData.system.trim(),
      model: formData.model,
      provider: formData.provider,
      creativity: formData.creativity,
      max_tokens: formData.max_tokens,
      tools: formData.tools,
      widget_settings: {
        ...formData.widget_settings,
        primary_color: formData.primaryColor,
        secondary_color: formData.secondaryColor,
        logo_url: formData.logo_url,
      },
      knowledges: formData.knowledges || []
    };

    if (forceSaveTools) {
      agentData.tools = forceSaveTools;
    }

    setSaveLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let response;
      if (isEditMode) {
        response = await agentService.updateAgent(agentId, agentData);
        setSuccess('Funcionário atualizado com sucesso! 🎉');
        
        // Recarrega o chat preview após salvar
        setTimeout(() => {
          reloadChatPreview();
        }, 500);
      } else {
        response = await agentService.createAgent(agentData);
        setSuccess('Funcionário contratado com sucesso! 🎉');
        
        // Redirect to edit mode after creation
        setTimeout(() => {
          navigate(`/my-agents`);
        }, 500);
      }
    } catch (err) {
      console.error('Error saving agent:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar funcionário.');
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-blue-50">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-green-100 rounded-3xl mb-4">
            <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
          </div>
          <p className="text-xl text-gray-600">Carregando dados do funcionário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50">
      {/* Header Gradiente */}
      <div className="bg-gradient-to-r from-green-800 to-green-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/my-agents')}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <Briefcase className="w-8 h-8" />
                  {isEditMode ? 'Gerenciar Funcionário' : 'Contratar Novo Funcionário'}
                </h1>
                <p className="text-lg opacity-90 mt-1">
                  {isEditMode ? 'Configure as habilidades do seu funcionário digital' : 'Crie um assistente que trabalha 24/7'}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => handleSave()}
              disabled={saveLoading}
              className="inline-flex items-center px-6 py-3 bg-white text-green-600 text-lg font-semibold rounded-2xl hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
            >
              {saveLoading ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-6 h-6 mr-3" />
                  {isEditMode ? 'Salvar Alterações' : 'Finalizar Contratação'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-start shadow-lg">
            <AlertCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-lg">{error}</div>
            <button onClick={() => setError(null)} className="ml-3 hover:bg-red-100 p-1 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-2xl flex items-start shadow-lg">
            <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-lg">{success}</div>
            <button onClick={() => setSuccess(null)} className="ml-3 hover:bg-green-100 p-1 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs simplificadas */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl mb-8 overflow-hidden">
          <div className="flex">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 px-6 py-4 text-lg font-semibold transition-all flex items-center justify-center gap-3 ${
                activeTab === 'profile'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <UserCircle className="w-6 h-6" />
              Perfil
            </button>
            <button
              onClick={() => setActiveTab('instructions')}
              className={`flex-1 px-6 py-4 text-lg font-semibold transition-all flex items-center justify-center gap-3 ${
                activeTab === 'instructions'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-6 h-6" />
              Instruções
            </button>
            <button
              onClick={() => setActiveTab('knowledge')}
              className={`flex-1 px-6 py-4 text-lg font-semibold transition-all flex items-center justify-center gap-3 ${
                activeTab === 'knowledge'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Brain className="w-6 h-6" />
              Conhecimentos
            </button>
            <button
              onClick={() => setActiveTab('habilities')}
              className={`flex-1 px-6 py-4 text-lg font-semibold transition-all flex items-center justify-center gap-3 ${
                activeTab === 'habilities'
                  ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Boxes className="w-6 h-6" />
              Habilidades
            </button>
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'profile' ? (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Coluna Principal - Formulário */}
            <div className="lg:col-span-2 space-y-8">
              {/* Card de Identidade */}
              <AgentIdentityCard 
                formData={formData}
                onFieldChange={handleFieldChange}
              />

              <TooltipConfig 
                tooltipSettings={{
                  message: formData.widget_settings?.tooltipMessage || '',
                  position: formData.widget_settings?.tooltipPosition || 'left',
                  backgroundColor: formData.widget_settings?.tooltipBackgroundColor || '#6B46C1',
                  textColor: formData.widget_settings?.tooltipTextColor || '#FFFFFF'
                }}
                onChange={(settings) => {
                  handleFieldChange('widget_settings', {
                    ...formData.widget_settings,
                    tooltipMessage: settings.message,
                    tooltipPosition: settings.position,
                    tooltipBackgroundColor: settings.backgroundColor,
                    tooltipTextColor: settings.textColor
                  }, false);
                }}
              />

              <FormBuilder 
                formSettings={formData.widget_settings?.form}
                onChange={(formSettings) => {
                  handleFieldChange('widget_settings', {
                    ...formData.widget_settings,
                    form: formSettings
                  }, false);
                }}
              />

              <WelcomeMessageConfig 
                welcomeMessage={formData.widget_settings?.welcome_message}
                onChange={(message) => {
                  handleFieldChange('widget_settings', {
                    ...formData.widget_settings,
                    welcome_message: message
                  }, false);
                }}
              />
            </div>

            {/* Coluna Lateral - Settings e Dicas */}
            <div className="space-y-8">
              {/* Card de Configurações */}
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 sticky top-8">
                <h4 className="text-xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <Sparkles className="w-6 h-6 text-indigo-600" />
                  Configurações Avançadas
                </h4>

                {/* Model Settings */}
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-600 font-medium flex items-center gap-2 mb-2">
                      <Type className="w-4 h-4" />
                      Limite de Palavras
                    </label>
                    <TokensSlider
                      value={formData.max_tokens}
                      onChange={(value) => handleFieldChange('max_tokens', value)}
                    />
                  </div>
                  
                  <div>
                    <label className="text-gray-600 font-medium flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4" />
                      Criatividade
                    </label>
                    <CreativitySlider
                      value={formData.creativity}
                      onChange={(value) => handleFieldChange('creativity', value)}
                    />
                  </div>
                </div>

                {/* Dicas */}
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Dica</h5>
                      <p className="text-sm text-gray-600">
                        Após salvar as alterações, clique no botão de refresh para atualizar o chat ao lado.
                      </p>
                    </div>
                  </div>
                  
                  {isEditMode && (
                    <button
                      onClick={reloadChatPreview}
                      className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl hover:from-indigo-700 hover:to-blue-700 transition-all flex items-center justify-center gap-2 font-semibold"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Atualizar Preview
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === "instructions" ? (
          // Tab de Instruções de Trabalho
          <div className="space-y-8">
            <SystemPromptEditor
              value={formData.system}
              onChange={(value) => handleFieldChange('system', value, false)}
              tools={formData.tools}
              agentId={agentId}
            />
          </div>
        ) : activeTab === 'knowledge' ? (
          <KnowledgeBaseManager 
            knowledges={formData.knowledges || []}
            onChange={(knowledges) => handleFieldChange('knowledges', knowledges, false)}
            onSave={handleSave}
          />
        ) : (   
          // Card de Habilidades Especiais
          <div className="space-y-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-6">
                <Wand2 className="w-8 h-8 text-green-600" />
                Habilidades Especiais
              </h3>
              
              <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-100">
                <p className="text-lg text-gray-700 flex items-start gap-2">
                  <Zap className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  Adicione superpoderes ao seu funcionário! Escolha habilidades como gerar imagens, converter áudio, acessar dados e muito mais.
                </p>
              </div>

              <AgentTools
                tools={formData.tools}
                onToolsChange={(tools, forceSaveTool) => handleFieldChange('tools', tools, forceSaveTool)}
                agentId={agentId}
              />
            </div>
          </div>
        )}
      </div>

      {/* Chat Widget Integrado - Apenas em modo de edição */}
      {isEditMode && agentId && (
        <>
          <iframe
            ref={iframeRef}
            id="agent-chat-preview"
            src={`${window.location.origin}/agent-chat/iframe/${agentId}?apiKey=${localStorage.getItem('authToken')}`}
            style={{
              position: 'fixed',
              border: 'none',
              zIndex: 9999,
              bottom: '20px',
              right: '20px',
              transition: 'all 0.3s ease',
              borderRadius: '20px',
            }}
          />
          
          {/* Indicador de Preview */}
         <div
            style={{
              position: 'fixed',
              bottom: isChatOpen ? '620px' : '170px',
              right: '20px',
              zIndex: 9998,
              transition: 'all 0.3s ease',
            }}
          >
            <div className="bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-semibold shadow-lg flex items-center gap-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Preview ao vivo
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Tokens/Max Words Slider Component
 */
const TokensSlider = ({ value, onChange }) => {
  const wordsEstimate = Math.round(value * 0.95);
  const percentage = ((value - 10) / (4500 - 10)) * 100;
  
  const getLabel = () => {
    if (value < 1500) return 'Rápido e objetivo';
    if (value < 2500) return 'Resposta completa';
    if (value < 4500) return 'Com mais detalhes';
    return 'Contador de histórias';
  };

  const getColor = () => {
    if (value < 1500) return 'from-blue-500 to-indigo-600';
    if (value < 2500) return 'from-indigo-500 to-purple-600';
    if (value < 4500) return 'from-purple-500 to-red-600';
    return 'from-red-500 to-red-600';
  };

  return (
    <div className="space-y-2">
      <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${getColor()} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
        <input
          type="range"
          min="50"
          max="4500"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
        />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm font-bold text-indigo-500">{getLabel()}</span>
        <span className="text-sm font-medium bg-indigo-100 text-green-700 px-3 py-1 rounded-full">
          ~{wordsEstimate} palavras
        </span>
      </div>
    </div>
  );
};

/**
 * Creativity Slider Component
 */
const CreativitySlider = ({ value, onChange }) => {
  const percentage = value * 100;
  
  const getLabel = () => {
    if (value < 0.3) return 'Preciso';
    if (value < 0.7) return 'Equilibrado';
    return 'Criativo';
  };

  const getColor = () => {
    if (value < 0.3) return 'from-blue-500 to-green-600';
    if (value < 0.7) return 'from-indigo-500 to-indigo-600';
    return 'from-indigo-500 to-green-600';
  };

  return (
    <div className="space-y-2">
      <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r ${getColor()} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
        <input
          type="range"
          min="0"
          max="100"
          value={percentage}
          onChange={(e) => onChange(e.target.value / 100)}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
        />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm font-bold text-indigo-500">{getLabel()}</span>
        <span className="text-sm font-medium bg-indigo-100 text-green-700 px-3 py-1 rounded-full">
          {Math.round(percentage)}%
        </span>
      </div>
    </div>
  );
};

export default AgentManager;